import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { message, Spin } from "antd";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetBalanceQuery,
  useGetEventDetailQuery,
  useGetMyIpQuery,
  useGetTvChannelQuery,
  useMyBetsQuery,
  usePlaceBetMutation,
} from "../../../store/service/clientBetService";
import ClientBetSlip from "./ClientBetSlip";
import ClientLiveScore from "./ClientLiveScore";
import { calcRunnerPnl, getMatchBets } from "./clientBetPnl";
import {
  eventFingerprint,
  isSuspendedPrice,
  parseEventMarkets,
} from "./clientMatchMarkets";
import { marketTypeFromLabel, parseTeams, normalizeBetLimits } from "./clientUtils";
import "./Client.scss";

const getAllMatchBets = (bets, matchId) => {
  const id = Number(matchId);
  return (bets || []).filter((b) => Number(b.matchId) === id);
};

const betTypeLabel = (type, market) => {
  if (market === "fancy" || String(market).includes("fancy")) {
    return type === "back" ? "YES" : "NO";
  }
  return type === "back" ? "LAGAI" : "KHAI";
};

const OddButton = memo(function OddButton({
  className,
  active,
  suspended,
  rate,
  size,
  onClick,
}) {
  if (suspended) {
    return <div className={`play-odd-cell play-odd-suspended ${className || ""}`}>—</div>;
  }
  return (
    <button
      type="button"
      className={`play-odd-cell ${className || ""} ${active ? "active" : ""}`}
      onClick={onClick}>
      <span className="fancy-rate">{rate}</span>
      {size ? <span className="fancy-size">{size}</span> : null}
    </button>
  );
});

const ClientMatch = () => {
  const { matchId } = useParams();
  const nav = useNavigate();

  // 3s poll — 1s caused constant re-render / header flicker.
  const { data: eventData, isLoading } = useGetEventDetailQuery(matchId, {
    pollingInterval: 3000,
    refetchOnMountOrArgChange: false,
    refetchOnFocus: false,
    refetchOnReconnect: true,
  });
  const { data: balanceData } = useGetBalanceQuery(undefined, {
    pollingInterval: 15000,
  });
  const { data: myBetsData } = useMyBetsQuery(undefined, {
    pollingInterval: 8000,
  });
  const { data: chids } = useGetTvChannelQuery(matchId, {
    refetchOnMountOrArgChange: false,
  });
  const { data: userIp } = useGetMyIpQuery();
  const [placeBet, { isLoading: placing }] = usePlaceBetMutation();

  const [activeTab, setActiveTab] = useState("live");
  const [selection, setSelection] = useState(null);
  const [betType, setBetType] = useState(null);
  const [odds, setOdds] = useState(null);
  const [marketLabel, setMarketLabel] = useState("Bookmaker");
  const [slipKey, setSlipKey] = useState(null);
  const [showTv, setShowTv] = useState(false);
  const [tvUrl, setTvUrl] = useState(null);

  // Hold last good payload so a bad poll never blanks / blinks the page.
  const lastGoodRef = useRef(null);
  const lastFpRef = useRef("");
  const [stableEvent, setStableEvent] = useState(null);

  useEffect(() => {
    if (!eventData) return;
    const fp = eventFingerprint(eventData);
    if (fp && fp === lastFpRef.current) return;
    lastFpRef.current = fp;
    lastGoodRef.current = eventData;
    setStableEvent(eventData);
  }, [eventData]);

  const markets = useMemo(
    () => parseEventMarkets(stableEvent || lastGoodRef.current),
    [stableEvent]
  );

  const {
    bookmakers,
    fancyList,
    matchOdds,
    matchName,
    inPlay,
    bmMin,
    bmMax,
    fancyMin,
    fancyMax,
  } = markets;

  const balance = balanceData?.data?.balance ?? 0;
  const channelId = chids?.data?.channelId;

  const openMatchBets = useMemo(
    () => getMatchBets(myBetsData?.data?.betList, matchId),
    [myBetsData, matchId]
  );
  const allMatchBets = useMemo(
    () => getAllMatchBets(myBetsData?.data?.betList, matchId),
    [myBetsData, matchId]
  );
  const openCount = openMatchBets.length;
  const runnerNames = useMemo(() => bookmakers.map((r) => r.nation), [bookmakers]);
  const { team1 } = useMemo(() => parseTeams(matchName), [matchName]);

  const moLimits = useMemo(
    () => normalizeBetLimits(matchOdds?.minBet ?? bmMin, matchOdds?.maxBet ?? bmMax),
    [matchOdds, bmMin, bmMax]
  );

  const slipLimits = useMemo(() => {
    if (marketLabel === "Fancy") return normalizeBetLimits(fancyMin, fancyMax);
    if (marketLabel === "Match Odds") return moLimits;
    return normalizeBetLimits(bmMin, bmMax);
  }, [marketLabel, fancyMin, fancyMax, moLimits, bmMin, bmMax]);
  const slipMin = slipLimits.min;
  const slipMax = slipLimits.max;

  useEffect(() => {
    if (!showTv || !channelId) return;
    let cancelled = false;

    const loadTv = async () => {
      try {
        const response = await fetch("https://api2.dbm9.com/api/tv-stream", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ channel: channelId, ipv4: userIp?.ip ?? "" }),
        });
        if (cancelled) return;
        const result = await response.json();
        if (result.status === 1) setTvUrl(result.data);
      } catch {
        if (!cancelled) message.warning("Live TV is unavailable right now");
      }
    };

    loadTv();
    return () => {
      cancelled = true;
    };
  }, [showTv, channelId, userIp]);

  // Clear slip when leaving this match
  useEffect(() => {
    setSelection(null);
    setBetType(null);
    setOdds(null);
    setMarketLabel("Bookmaker");
    setSlipKey(null);
    setActiveTab("live");
    lastFpRef.current = "";
    lastGoodRef.current = null;
    setStableEvent(null);
  }, [matchId]);

  const clearSlip = useCallback(() => {
    setSelection(null);
    setBetType(null);
    setOdds(null);
    setMarketLabel("Bookmaker");
    setSlipKey(null);
  }, []);

  const switchTab = useCallback(
    (id) => {
      setActiveTab(id);
      clearSlip();
    },
    [clearSlip]
  );

  const pickOdds = useCallback((team, type, value, market, key) => {
    if (isSuspendedPrice(value)) {
      message.warning("This market is suspended");
      return;
    }
    setSelection(team);
    setBetType(type);
    setOdds(Number(value));
    setMarketLabel(market);
    setSlipKey(key);
  }, []);

  const handlePlaceBet = useCallback(
    async (stake) => {
      try {
        const res = await placeBet({
          matchId: Number(matchId),
          matchName: `${matchName} (${marketLabel})`,
          selection,
          betType,
          odds,
          stake,
          marketType: marketTypeFromLabel(marketLabel),
        }).unwrap();

        message.success(res?.message || "Bet placed successfully");
        clearSlip();
      } catch (err) {
        message.error(err?.data?.message || "Could not place bet");
      }
    },
    [placeBet, matchId, matchName, marketLabel, selection, betType, odds, clearSlip]
  );

  const renderSlip = (key) =>
    slipKey === key && selection ? (
      <ClientBetSlip
        key={key}
        compact
        selection={selection}
        betType={betType}
        odds={odds}
        marketType={marketTypeFromLabel(marketLabel)}
        minBet={slipMin}
        maxBet={slipMax}
        balance={balance}
        placing={placing}
        onCancel={clearSlip}
        onPlace={handlePlaceBet}
      />
    ) : null;

  if (isLoading && !stableEvent && !lastGoodRef.current) {
    return (
      <div className="play-loading">
        <Spin size="large" tip="Loading match…" />
      </div>
    );
  }

  return (
    <div className="play-match-page">
      <div className="play-top-bar">
        <button type="button" className="play-back-link" onClick={() => nav("/play")}>
          ← Back
        </button>
        <div className="play-match-title">{matchName}</div>
        {inPlay ? <span className="play-live-pill">LIVE</span> : null}
        {channelId ? (
          <button type="button" className="play-tv-chip" onClick={() => setShowTv((v) => !v)}>
            {showTv ? "Hide" : "TV"}
          </button>
        ) : null}
      </div>

      {showTv && tvUrl ? (
        <div className="play-tv-frame">
          <iframe title="Live TV" src={tvUrl} allowFullScreen />
        </div>
      ) : null}

      <div className="play-match-tabs play-match-tabs--2">
        <button
          type="button"
          className={activeTab === "live" ? "active" : ""}
          onClick={() => switchTab("live")}>
          Live
        </button>
        <button
          type="button"
          className={activeTab === "bets" ? "active" : ""}
          onClick={() => switchTab("bets")}>
          My Bets
          {allMatchBets.length ? <i>{allMatchBets.length}</i> : null}
        </button>
      </div>

      {openCount > 0 && activeTab !== "bets" ? (
        <button type="button" className="play-open-chip" onClick={() => switchTab("bets")}>
          {openCount} open bet{openCount > 1 ? "s" : ""} · View
        </button>
      ) : null}

      {activeTab === "live" ? (
        <>
          <ClientLiveScore matchId={matchId} />

          <div className="play-section-head">
            Bookmaker · {bmMin}-{bmMax}
          </div>
          <div className="play-odds-table">
            <div className="play-odds-head">
              <div className="play-odds-team-col" />
              <div className="play-lagai-head">Lagai</div>
              <div className="play-khai-head">Khai</div>
            </div>

            {bookmakers.length > 0 ? (
              bookmakers.map((runner) => {
                const pnl = calcRunnerPnl(openMatchBets, runner.nation, runnerNames);
                const backKey = `bm-${runner._key}-back`;
                const layKey = `bm-${runner._key}-lay`;
                const rowActive = slipKey === backKey || slipKey === layKey;
                const backSus =
                  isSuspendedPrice(runner.b1, runner.gstatus) ||
                  String(runner.gstatus || "").toUpperCase() === "SUSPENDED";
                const laySus =
                  isSuspendedPrice(runner.l1, runner.gstatus) ||
                  String(runner.gstatus || "").toUpperCase() === "SUSPENDED";

                return (
                  <div key={runner._key} className="play-odds-block">
                    <div className={`play-odds-row ${rowActive ? "selected" : ""}`}>
                      <div className="play-team-cell">
                        <div className="play-team-name">{runner.nation?.toUpperCase()}</div>
                        <div className={`play-team-pnl ${pnl >= 0 ? "pos" : "neg"}`}>
                          {pnl.toFixed(2)}
                        </div>
                      </div>
                      <OddButton
                        className="lagai"
                        active={slipKey === backKey}
                        suspended={backSus}
                        rate={runner.b1}
                        size={runner.bs1}
                        onClick={() =>
                          pickOdds(runner.nation, "back", runner.b1, "Bookmaker", backKey)
                        }
                      />
                      <OddButton
                        className="khai"
                        active={slipKey === layKey}
                        suspended={laySus}
                        rate={runner.l1}
                        size={runner.ls1}
                        onClick={() =>
                          pickOdds(runner.nation, "lay", runner.l1, "Bookmaker", layKey)
                        }
                      />
                    </div>
                    {renderSlip(backKey)}
                    {renderSlip(layKey)}
                  </div>
                );
              })
            ) : (
              <div className="play-fallback-row">
                <div className="play-team-cell">
                  <div className="play-team-name">{team1?.toUpperCase() || "—"}</div>
                </div>
                <div className="play-odd-cell play-odd-suspended">—</div>
                <div className="play-odd-cell play-odd-suspended">—</div>
              </div>
            )}
          </div>

          {matchOdds?.runners?.length > 0 ? (
            <>
              <div className="play-section-head yellow">
                Match Odds · {moLimits.min}-{moLimits.max}
              </div>
              <div className="play-mo-list">
                {matchOdds.runners.map((r) => {
                  const back = r?.ex?.availableToBack?.[0]?.price;
                  const lay = r?.ex?.availableToLay?.[0]?.price;
                  const backKey = `mo-${r.selectionId}-back`;
                  const layKey = `mo-${r.selectionId}-lay`;
                  return (
                    <div key={r.selectionId} className="play-mo-block">
                      <div className="play-mo-row">
                        <div className="play-mo-name">{r.name}</div>
                        <OddButton
                          className="lagai tiny"
                          active={slipKey === backKey}
                          suspended={isSuspendedPrice(back)}
                          rate={back}
                          onClick={() => pickOdds(r.name, "back", back, "Match Odds", backKey)}
                        />
                        <OddButton
                          className="khai tiny"
                          active={slipKey === layKey}
                          suspended={isSuspendedPrice(lay)}
                          rate={lay}
                          onClick={() => pickOdds(r.name, "lay", lay, "Match Odds", layKey)}
                        />
                      </div>
                      {renderSlip(backKey)}
                      {renderSlip(layKey)}
                    </div>
                  );
                })}
              </div>
            </>
          ) : null}

          <div className="play-section-head fancy">
            Fancy · {fancyList.length} · {fancyMin}-{fancyMax}
          </div>
          <div className="play-fancy-table">
            <div className="play-fancy-head">
              <div className="play-fancy-name-col">Session</div>
              <div className="play-khai-head">No</div>
              <div className="play-lagai-head">Yes</div>
            </div>
            {fancyList.length === 0 ? (
              <div className="play-suspended" style={{ padding: 12 }}>
                No fancy sessions for this match right now
              </div>
            ) : (
              fancyList.map((f) => {
                const suspended = String(f.gstatus || "").toUpperCase() === "SUSPENDED";
                const noKey = `f-${f._key}-lay`;
                const yesKey = `f-${f._key}-back`;
                return (
                  <div key={f._key} className="play-fancy-block">
                    <div className="play-fancy-row">
                      <div className="play-fancy-name">{f.nation}</div>
                      {suspended ? (
                        <div className="play-suspended" style={{ gridColumn: "2 / -1" }}>
                          SUSPENDED
                        </div>
                      ) : (
                        <>
                          <OddButton
                            className="khai small fancy-odd"
                            active={slipKey === noKey}
                            suspended={isSuspendedPrice(f.l1)}
                            rate={f.l1}
                            size={f.ls1}
                            onClick={() => pickOdds(f.nation, "lay", f.l1, "Fancy", noKey)}
                          />
                          <OddButton
                            className="lagai small fancy-odd"
                            active={slipKey === yesKey}
                            suspended={isSuspendedPrice(f.b1)}
                            rate={f.b1}
                            size={f.bs1}
                            onClick={() => pickOdds(f.nation, "back", f.b1, "Fancy", yesKey)}
                          />
                        </>
                      )}
                    </div>
                    {renderSlip(noKey)}
                    {renderSlip(yesKey)}
                  </div>
                );
              })
            )}
          </div>
        </>
      ) : null}

      {activeTab === "bets" ? (
        <div className="play-bets-tab">
          {allMatchBets.length === 0 ? (
            <div className="lotus-empty">No bets on this match yet</div>
          ) : (
            <>
              <div className="play-bets-summary">
                <span>
                  Open <strong>{openCount}</strong>
                </span>
                <span>
                  Total <strong>{allMatchBets.length}</strong>
                </span>
              </div>
              <div className="play-my-bets-list">
                {allMatchBets.map((bet) => (
                  <div key={bet.betId} className={`play-my-bet-item st-${bet.status}`}>
                    <div className="play-my-bet-main">
                      <strong>{bet.selection}</strong>
                      <span>
                        {betTypeLabel(bet.betType, bet.marketType)} @ {bet.odds}
                        {bet.placedOn ? ` · ${bet.placedOn}` : ""}
                      </span>
                    </div>
                    <div className="play-my-bet-side">
                      <em>{bet.status}</em>
                      <span>Stake {bet.stake}</span>
                      {bet.potentialWin != null ? (
                        <span className="play-my-bet-pnl">P/L {bet.potentialWin}</span>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default ClientMatch;
